import { Table } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import ArrowLeftIcon from '@/components/icons/ArrowLeftIcon';
import ArrowRightIcon from '@/components/icons/ArrowRightIcon';

type DataTablePaginationProps<TData> = {
	table: Table<TData>;
	range?: number;
};

export function ETablePagination<TData>({
	table,
	range = 2,
}: DataTablePaginationProps<TData>) {
	const currentPage = table.getState().pagination.pageIndex + 1;
	const pageCount = table.getPageCount();

	const getVisiblePages = (current: number, total: number, range: number) => {
		const pages: number[] = [];
		for (let i = 1; i <= total; i++) {
			if (
				i === 1 ||
				i === total || 
				(i >= current - range && i <= current + range) 
			) {
				pages.push(i);
			}
		}
		return pages;
	};

	const addEllipses = (pages: number[]) => {
		const result: (number | '...')[] = [];
		for (let i = 0; i < pages.length; i++) {
			if (i > 0 && pages[i] !== pages[i - 1] + 1) {
				result.push('...');
			}
			result.push(pages[i]);
		}
		return result;
	};

	const visiblePages = getVisiblePages(currentPage, pageCount, range);
	const pagesWithEllipses = addEllipses(visiblePages);

	return (
		<div className='flex items-center justify-center gap-2'>
			<Button
				variant='outline'
				className={`h-fit max-h-11 max-w-11 p-3`}
				onClick={() => table.previousPage()}
				disabled={!table.getCanPreviousPage()}>
				<ArrowLeftIcon />
			</Button>
			{pagesWithEllipses.map((page, index) =>
				page === '...' ? (
					<Button
						key={index}
						variant='outline'
						className={`h-fit max-h-11 min-w-11 p-3`}>
						...
					</Button>
				) : (
					<Button
						key={index}
						variant={`${
							currentPage === page ? 'default' : 'outline'
						}`}
						className={`h-fit max-h-11 max-w-11 px-5 py-3`}
						onClick={() => table.setPageIndex(page - 1)}>
						{page}
					</Button>
				),
			)}
			<Button
				variant='outline'
				className={`h-fit max-h-11 max-w-11 p-3`}
				onClick={() => table.nextPage()}
				disabled={!table.getCanNextPage()}>
				<ArrowRightIcon />
			</Button>
		</div>
	);
}
